import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Button,
  Tooltip,
  IconButton,
  Stack,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
} from "@mui/material";
import { thumbzApi, useThumbzApi } from "src/services/thumbz-api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "src/config/query-key";
import { ICustomer } from "src/services/thumbz-base-api";
import toast from "react-hot-toast";
import { getServerErrorArrayMessage } from "src/utils/get-server-error-message";
import { Add, Delete, Edit, MoreVertOutlined } from "@mui/icons-material";
import { CustomerUpsertForm } from "src/sections/customers/customer-upsert-form";

import { getInitials } from "src/utils/get-initials";
import { formatPhoneNumber } from "src/utils/format-phone-number";
import { TutorialVideoButton } from "src/components/tutorial/tutorial-video-button";
import { tutorialVideoById } from "src/constants/tutorials";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { Alert, AlertTitle } from "@mui/material";
import { WarningAmber, InfoOutlined } from "@mui/icons-material";
import { useAppSelector } from "src/app/hooks";
import { organizationSlice } from "src/features/organization/organizationSlice";
import { authSlice } from "src/features/auth/authSlice";
import { useUserRole } from "src/hooks/use-user-roles";

interface WorkspaceCustomersProps {
  wrk_id: string;
}

export const WorkspaceCustomers: React.FC<WorkspaceCustomersProps> = ({ wrk_id }) => {
  const [modalopen, setModalopen] = useState<boolean>(false);
  const [rowData, setRowData] = useState<ICustomer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<ICustomer | null>(null);
  const queryClient = useQueryClient();
  const { api } = useThumbzApi();

  const organization = useAppSelector(organizationSlice.selectors.selectedOrganization);
  const loggedUser = useAppSelector(authSlice.selectors.selectUser);
  const { loggedUserRole } = useUserRole(
    organization?.collaborators ?? null,
    loggedUser?.usu_id ?? null,
    loggedUser?.usu_id ?? "",
  );
  // const canManageCustomers = useMemo(
  //   () => loggedUserRole === "OWNER" || loggedUserRole === "MEMBER",
  //   [loggedUserRole],
  // );
  const canManageCustomers = true;

  function handleCustomerEdit(customer: ICustomer): void {
    setSelectedCustomer(customer);
    setModalopen(true);
  }

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuCustomer, setMenuCustomer] = useState<ICustomer | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, customer: ICustomer) => {
    setMenuAnchor(event.currentTarget);
    setMenuCustomer(customer);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuCustomer(null);
  };

  function clearCache() {
    queryClient.invalidateQueries({
      queryKey: queryKeys.customer.customerControllerList({ wrk_id }).queryKey,
    });
    if (organization?.org_id) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.approval.approvalControllerList({ wrk_id, org_id: organization.org_id })
          .queryKey,
      });
    }
  }

  const customersQuery = useQuery({
    queryKey: queryKeys.customer.customerControllerList({ wrk_id }).queryKey,
    queryFn: () => api.customer.customerControllerList({ wrk_id }),
    enabled: !!wrk_id,
  });

  const deleteCustomerMutation = useMutation({
    mutationFn: api.customer.customerControllerDelete,
    onSuccess: () => {
      clearCache();
      toast.success("Aprovador excluído com sucesso");
    },
    onError: (error) =>
      getServerErrorArrayMessage(error).forEach((message) => toast.error(message)),
  });

  interface IDeletionImpact {
    active_approvals_count: number;
    affected_stages_count: number;
    pending_items_count: number;
  }

  const [impactDialogOpen, setImpactDialogOpen] = useState(false);
  const [impactLoading, setImpactLoading] = useState(false);
  const [impactData, setImpactData] = useState<IDeletionImpact | null>(null);
  const [impactTarget, setImpactTarget] = useState<ICustomer | null>(null);

  useEffect(() => {
    if (customersQuery.data) {
      setRowData(customersQuery.data);
    }
  }, [customersQuery.data]);

  const handleDelete = (customer: ICustomer) => {
    setImpactTarget(customer);
    setImpactDialogOpen(true);
    setImpactLoading(true);
    thumbzApi.instance
      .get<IDeletionImpact>(`/customer/${customer.cust_id}/deletion-impact`, {
        params: { wrk_id },
      })
      .then((res) => {
        setImpactData(res.data);
      })
      .catch((error) => {
        getServerErrorArrayMessage(error).forEach((message) => toast.error(message));
      })
      .finally(() => setImpactLoading(false));
  };

  const handleAdd = () => {
    setSelectedCustomer(null);
    setModalopen(true);
  };

  function handleCustomerUpsertSuccess(customer: ICustomer): void {
    clearCache();
    setModalopen(false);
  }

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        spacing={1}
      >
        <TutorialVideoButton
          video={tutorialVideoById["add-approver"]}
          buttonLabel="Ver tutorial"
          buttonProps={{ size: "small" }}
        />
        <Tooltip title="Novo aprovador">
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={handleAdd}
            startIcon={<Add />}
          >
            Novo aprovador
          </Button>
        </Tooltip>
      </Stack>

      <Table sx={{ minWidth: 500 }} size="medium">
        <TableHead>
          <TableRow>
            <TableCell>Aprovador</TableCell>
            <TableCell>Telefone</TableCell>
            <TableCell align="right">Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rowData.slice(0, 10).map((customer) => (
            <TableRow key={customer.cust_id} hover>
              <TableCell>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Avatar
                    src={customer.cust_photo?.ast_url || undefined}
                    sx={{ width: 35, height: 35 }}
                  >
                    {getInitials(customer.cust_name)}
                  </Avatar>
                  <Stack>
                    <Typography variant="subtitle2">{customer.cust_name}</Typography>
                    <Typography color="text.secondary" variant="body2" noWrap>
                      {customer.cust_email}
                    </Typography>
                  </Stack>
                </Stack>
              </TableCell>
              <TableCell>
                {customer.cust_mobile_phone
                  ? formatPhoneNumber({
                      phoneNumber: customer.cust_mobile_phone,
                      countryCode: customer.country_code,
                    })
                  : ""}
              </TableCell>
              <TableCell align="right">
                {canManageCustomers ? (
                  <IconButton onClick={(e) => handleMenuOpen(e, customer)}>
                    <MoreVertOutlined />
                  </IconButton>
                ) : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            if (menuCustomer) handleCustomerEdit(menuCustomer);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Editar</ListItemText>
        </MenuItem>
        {canManageCustomers ? (
          <MenuItem
            onClick={() => {
              if (menuCustomer) handleDelete(menuCustomer);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <Delete fontSize="small" />
            </ListItemIcon>
            <ListItemText>Excluir</ListItemText>
          </MenuItem>
        ) : null}
      </Menu>
      <CustomerUpsertForm
        key={selectedCustomer?.cust_id}
        open={modalopen}
        customer={selectedCustomer}
        wrk_id={wrk_id}
        showSelect={false}
        onClose={() => setModalopen(false)}
        onSuccess={handleCustomerUpsertSuccess}
      />

      <Dialog
        open={impactDialogOpen}
        onClose={() => setImpactDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <WarningAmber color="error" />
            <Typography variant="h6">Excluir Aprovador</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Você está prestes a excluir o aprovador {impactTarget?.cust_name}
          </Typography>

          {impactLoading ? (
            <Stack spacing={1} sx={{ py: 2 }}>
              <Typography variant="body2">Carregando impacto...</Typography>
            </Stack>
          ) : impactData ? (
            <Stack spacing={2} sx={{ py: 1 }}>
              <Alert
                severity="warning"
                variant="standard"
                sx={{ borderRadius: 2 }}
                icon={<WarningAmber />}
              >
                <AlertTitle>Aprovador está associado a processos</AlertTitle>
                Este aprovador está vinculado a {impactData.active_approvals_count} processos de
                aprovação ativos.
              </Alert>

              <Alert
                severity="info"
                variant="standard"
                sx={{ borderRadius: 2 }}
                icon={<InfoOutlined />}
              >
                <AlertTitle>Atualização automática do progresso</AlertTitle>
                Ao excluir este aprovador, o progresso de todas as aprovações associadas será
                recalculado automaticamente para refletir a mudança. Os percentuais e etapas serão
                atualizados de acordo.
              </Alert>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              Não foi possível carregar o impacto da exclusão.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImpactDialogOpen(false)}>Cancelar</Button>
          <Button
            color="error"
            variant="contained"
            disabled={impactLoading || !impactTarget}
            onClick={() => {
              if (!impactTarget) return;
              deleteCustomerMutation.mutate(
                { cust_id: impactTarget.cust_id },
                {
                  onSuccess: () => {
                    setImpactDialogOpen(false);
                    setImpactTarget(null);
                    setImpactData(null);
                  },
                },
              );
            }}
          >
            Excluir Aprovador
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
